import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  Users,
  UserPlus,
  Crown,
  Shield,
  Eye,
  Pencil,
  MoreHorizontal,
  Mail,
  Clock,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useWorkspaceMembers, WorkspaceRole, useMyWorkspaceRole } from '@/hooks/useWorkspace';
import { InviteMemberModal } from './InviteMemberModal';
import { useAuthStore } from '@/stores/authStore';

interface TeamMembersListProps {
  workspaceId: string;
  ownerId: string;
}

const roleIcons: Record<WorkspaceRole, typeof Crown> = {
  admin: Shield,
  editor: Pencil,
  viewer: Eye,
};

const roleColors: Record<WorkspaceRole, string> = {
  admin: 'bg-primary/10 text-primary',
  editor: 'bg-blue-500/10 text-blue-600',
  viewer: 'bg-muted text-muted-foreground',
};

export function TeamMembersList({ workspaceId, ownerId }: TeamMembersListProps) {
  const { user } = useAuthStore();
  const { data: myRole } = useMyWorkspaceRole(workspaceId);
  const { members, invitations, updateMemberRole, removeMember, cancelInvitation } = useWorkspaceMembers(workspaceId);
  const [showInvite, setShowInvite] = useState(false);

  const isAdmin = myRole === 'admin';
  const isOwner = user?.id === ownerId;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Members
          </CardTitle>
          <CardDescription>
            {(members?.length || 0) + 1} member{(members?.length || 0) !== 0 ? 's' : ''}
          </CardDescription>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowInvite(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Invite
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Owner */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
          <div className="flex items-center gap-3">
            <Avatar>
              <AvatarFallback>
                <Crown className="h-4 w-4 text-amber-500" />
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium">
                {user?.id === ownerId ? 'You' : 'Owner'}
                {user?.id === ownerId && <span className="text-muted-foreground"> (You)</span>}
              </p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <Badge variant="outline" className="bg-amber-500/10 text-amber-600">
            <Crown className="h-3 w-3 mr-1" />
            Owner
          </Badge>
        </div>

        {/* Members */}
        {members?.map((member) => {
          const RoleIcon = roleIcons[member.role];
          const isSelf = member.userId === user?.id;

          return (
            <div key={member.id} className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-3">
                <Avatar>
                  <AvatarFallback>
                    <RoleIcon className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">
                    {member.email || `User ${member.userId.slice(0, 8)}`}
                    {isSelf && <span className="text-muted-foreground"> (You)</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Joined {member.joinedAt ? formatDistanceToNow(new Date(member.joinedAt), { addSuffix: true }) : 'recently'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {isAdmin && !isSelf ? (
                  <>
                    <Select
                      value={member.role}
                      onValueChange={(role) => updateMemberRole.mutate({ memberId: member.id, role: role as WorkspaceRole })}
                    >
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="editor">Editor</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => removeMember.mutate(member.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Remove Member
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                ) : (
                  <Badge variant="outline" className={roleColors[member.role]}>
                    <RoleIcon className="h-3 w-3 mr-1" />
                    {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                  </Badge>
                )}
              </div>
            </div>
          );
        })}

        {/* Pending Invitations */}
        {invitations && invitations.length > 0 && (
          <>
            <div className="pt-4 border-t">
              <p className="text-sm font-medium text-muted-foreground mb-3">Pending Invitations</p>
            </div>
            {invitations.map((invitation) => (
              <div key={invitation.id} className="flex items-center justify-between p-3 rounded-lg border border-dashed">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback>
                      <Mail className="h-4 w-4 text-muted-foreground" />
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{invitation.email}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Expires {formatDistanceToNow(new Date(invitation.expiresAt), { addSuffix: true })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Badge variant="outline">
                    {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}
                  </Badge>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => cancelInvitation.mutate(invitation.id)}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </CardContent>

      <InviteMemberModal
        workspaceId={workspaceId}
        open={showInvite}
        onOpenChange={setShowInvite}
      />
    </Card>
  );
}
